import type { Attribute } from '@/common/attribute';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import type { SupportedLocale } from '@/hoslog-intl/Translation';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import { StorePaymentLogsDetailsData } from '@/services/payment/payment.types';
import { ColumnDef } from '@tanstack/react-table';
import {
  getClubVerificationDisplay,
  getPaymentMethodDisplay,
  getPlanTypeLabel,
  getReservationTypeDisplay,
  getTransactionStatusDisplay,
} from '../constants/storePaymentLogStatus.constant';

export function getColumns(
  t: (key: string) => string,
  locale?: string,
  reservationPlanTypes: Attribute[] = [],
): ColumnDef<StorePaymentLogsDetailsData>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-foreground truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'customer',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.customer')} />,
      cell: ({ row }) => <div className="text-caption text-foreground truncate">{row.getValue('customer')}</div>,
    },
    {
      accessorKey: 'plan',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.plan')} />,
      cell: ({ row }) => {
        const plan = row.getValue<string>('plan');
        const supportedLocale = (locale as SupportedLocale) ?? 'ja';
        const label = getPlanTypeLabel(plan, reservationPlanTypes, supportedLocale);
        const display = label !== '-' ? label : plan || '-';
        return <div className="text-caption text-foreground truncate">{display}</div>;
      },
    },
    {
      id: 'noOfPeople',
      accessorKey: 'noOfPeople',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.numberOfPeople')} />
      ),
      cell: ({ row }) => {
        const count = row.original.noOfPeople ?? row.original.NumberOfPeople;
        return <div className="text-caption text-foreground truncate">{count != null ? `${count} 名` : '-'}</div>;
      },
    },
    {
      id: 'createdDate',
      accessorKey: 'visitDateTime',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeVisitDateAndTime')} />
      ),
      cell: ({ row }) => {
        const visitDateTime = row.original.visitDateTime;
        const endDateTime = row.original.endDateTime;
        if (!visitDateTime) return <div className="text-caption text-foreground truncate">-</div>;
        const formattedStart = formatDate(visitDateTime, 'YYYY/MM/DD HH:mm');
        const formattedEnd = endDateTime ? formatDate(endDateTime, 'HH:mm') : '';
        const display = formattedEnd ? `${formattedStart} ~ ${formattedEnd}` : formattedStart;
        return <div className="text-caption text-foreground truncate">{display}</div>;
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'paymentMethod',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.paymentMethod')} />
      ),
      cell: ({ row }) => {
        const paymentMethod = row.getValue<string>('paymentMethod');
        const { label, className } = getPaymentMethodDisplay(paymentMethod, locale);
        return <div className={`text-caption font-medium truncate ${className}`}>{label}</div>;
      },
    },
    {
      accessorKey: 'clubVerification',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.clubVerification')} />
      ),
      cell: ({ row }) => {
        const verification = row.getValue<string>('clubVerification');
        const { label, className } = getClubVerificationDisplay(verification, locale);
        return <div className={`text-caption font-medium truncate ${className}`}>{label}</div>;
      },
    },
    {
      accessorKey: 'transactionStatus',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.transactionStatus')} />
      ),
      cell: ({ row }) => {
        const status = row.getValue<string>('transactionStatus');
        const { label, className } = getTransactionStatusDisplay(status, locale);
        return <div className={`text-caption font-medium truncate ${className}`}>{label}</div>;
      },
    },
    {
      accessorKey: 'reservationType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservationType')} />
      ),
      cell: ({ row }) => {
        const reservationType = row.getValue<string>('reservationType');
        const display = getReservationTypeDisplay(reservationType, locale);
        return <div className="text-caption text-foreground truncate">{display}</div>;
      },
    },
    {
      accessorKey: 'planAmount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.planAmount')} />,
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('planAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'totalPlanAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeTotalPlanAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('totalPlanAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'totalClubPlanAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalClubPlanAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('totalClubPlanAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'adminPlanCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.adminPlanCommissionAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('adminPlanCommissionAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'totalAdminPlanCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalAdminPlanCommissionAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('totalAdminPlanCommissionAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'adminCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.adminCommissionAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('adminCommissionAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'totalAdminCommissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeTotalAdminCommissionAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue<number>('totalAdminCommissionAmount'))}
        </div>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'totalAdminPayments',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeTotalAdminPayments')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-link truncate pr-3">
          {formatAmount(row.getValue<number>('totalAdminPayments'))}
        </div>
      ),
      meta: { align: 'right' },
    },
  ];
}
